import { Injectable, Injector } from "@angular/core";
import { Observable } from "rxjs";
import { Catalog, PendingResourcesType, Roles, StatusPendingCatalog } from "src/app/common/enumerations/enumerations";
import { CanDeactivateResponse } from "src/app/controls/pending-changes-dialog/pending-changes-dialog-response";
import { PendingChangesDialogComponent } from "src/app/controls/pending-changes-dialog/pending-changes-dialog.component";
import { PendingResource, PendingResourceUsers, PendingCatalog, PendingCatalogType } from "src/app/services/pending-approvals/pending-approval";
import { BeamlineShutterRelation, DetailsUsers, Resource } from "src/app/components/catalogs/beamline-catalog/resource/resources";
import { UserCatalog } from "src/app/components/catalogs/user-catalog/services/user";
import { environment } from "src/environments/environment";
import { CatalogModel, CatalogModelList, ModifyingResult, Tuple } from "./catalog-model";
import { ScheduleBeamlineOverviewResource } from "src/app/services/schedule/schedule-beamline-overview-resource";
import { BaseService } from "src/app/common/base/base.service";
import { PrivilegeEnum } from "src/app/common/enumerations/privilege-enum";


@Injectable({
  providedIn: 'root'
})
export class CatalogService extends BaseService<CatalogModel, number> {

  public api: string = environment.urls.baseApiUrl + '/WorkFlowCatalogs';
  public apiPendingResources: string = environment.urls.baseApiUrl + '/PendingResources';
  public apiPendingApproval: string = environment.urls.baseApiUrl + '/PendingApproval';
  public userCatalogs?: UserCatalog[];

  constructor(
    protected override injector: Injector,
  ) {
    super(injector, '');
  }

  public currentDocSchedule!: ScheduleBeamlineOverviewResource;

  pendingCatalog?: PendingCatalog;


  GetPendingCatalogById(id: number, catalog: Catalog): Observable<CatalogModel> {
    return this.http.get<CatalogModel>(this.apiPendingApproval + '/' + id + '/' + catalog);
  }

  GetJoinCreated(catalog: Catalog, join: boolean): Observable<CatalogModelList> {
    return this.http.get<CatalogModelList>(this.api + '/All/' + catalog + '/' + join);
  }

  GetHistory(catalog: Catalog, id: number): Observable<CatalogModelList> {
    return this.http.get<CatalogModelList>(this.api + '/History/' + catalog + '/' + id);
  }

  GetHistoryByID(catalog: Catalog, id: number): Observable<CatalogModelList> {
    return this.http.get<CatalogModelList>(this.api + '/HistoryByID/' + catalog + '/' + id);
  }

  GetBeamlineShutter(beamlineId: number) {
    return this.http.get<BeamlineShutterRelation>(this.api + '/BeamlineShutter/' + beamlineId);
  }

  IsModifiyingById(id: number, catalog: Catalog): Observable<ModifyingResult> {
    return this.http.get<ModifyingResult>(this.api + '/IsModifiying/' + id + '/' + catalog);
  }

  GetAllPendingResources(): Observable<PendingResource[]> {
    return this.http.get<PendingResource[]>(this.apiPendingResources);
  }

  GetPendingResourcesUsersByResourceId(idRosurce: number, typePendingResource: PendingResourcesType): Observable<PendingResourceUsers> {
    return this.http.get<PendingResourceUsers>(this.apiPendingResources + '/version/users/' + idRosurce + '/' + typePendingResource);
  }

  IsFieldRepeated(catalogModel: CatalogModel, catalog: Catalog): Observable<Tuple> {
    return this.http.post<Tuple>(this.api + '/IsFieldRepeated/' + catalog, catalogModel);
  }

  IsFieldRepeatedResource(resourceName: string, type: number): Observable<boolean> {
    const resource: Resource = { id: 0, type, name: resourceName };
    return this.http.post<boolean>(this.api + '/IsFieldRepeatedResource/' + type, resource);
  }

  SaveAsDraft(catalogModel: CatalogModel, catalog: Catalog) {
    return this.http.post<number>(this.api + '/' + catalog, catalogModel);
  }

  SaveAsDraftType(catalogModel: CatalogModel, catalog: Catalog, typePendingResource: PendingResourcesType) {
    return this.http.post<number>(this.api + '/' + catalog + '/' + typePendingResource, catalogModel);
  }

  Approve(id: number, catalog: Catalog, type: PendingResourcesType = PendingResourcesType.Default): Observable<number> {
    return this.http.put<number>(this.api + '/Approve/' + id + '/' + catalog + '/' + type, null);
  }

  Unapprove(id: number, catalog: Catalog, disapprovalReason: string, type: PendingResourcesType = PendingResourcesType.Default) {
    return this.http.put(this.api + '/Disapprove/' + id + '/' + catalog + '/' + type, { reason: disapprovalReason });
  }

  RemoveRow(id: number, catalog: PendingCatalogType) {
    return this.http.delete(this.api + '/' + id + '/' + catalog);
  }

  RemoveRows(ids: number[], catalog: PendingCatalogType) {
    return this.http.patch(this.api + '/RemoveRows/' + catalog, ids);
  }

  CancelPending(id: number, catalog: Catalog, type: PendingResourcesType) {
    return this.http.delete(this.api + '/DeletePending/' + id + '/' + catalog + '/' + type);
  }

  getUserApprovals(id: number) {
    return this.http.get<DetailsUsers>(this.BASE_URL + '/AcceleratorCatalog/UserApprovals/' + id);
  }

  getLastPendingResource(
    beamlineShutter: BeamlineShutterRelation,
    type: PendingResourcesType
  ): PendingResource | undefined {
    const pendingResource = beamlineShutter?.pendingResources
      ?.filter(p => (p?.type === type || p?.type === PendingResourcesType.Both) && p?.status === StatusPendingCatalog.Pending)
      .at(-1);

    if (!pendingResource || pendingResource.status !== 1) return undefined;

    const isCreatedByCurrentUser = this.currentUser?.id === pendingResource.createdBy;

    const hasRPorSARole = this.hasRoles([Roles.RP, Roles.SA]);

    const hasCatalogAccess =
      this.hasPrivilege(PrivilegeEnum.BeamlineCatalog) &&
      (type === PendingResourcesType.Details || type === PendingResourcesType.Both);

    const hasRssDbAccess =
      (this.hasPrivilege(PrivilegeEnum.RSSTestDBAuth) ||
        this.hasPrivilege(PrivilegeEnum.RSSTestDBEdit) &&
        isCreatedByCurrentUser) &&
      (type === PendingResourcesType.Rss_Database || type === PendingResourcesType.Both);

    const isAuthorized = hasRPorSARole || hasCatalogAccess || hasRssDbAccess;

    const hasUnapprovedChanges =
      ((pendingResource.hasResourceChanges || !pendingResource.hasShieldingChanges) && !pendingResource.approvedBy) ||
      (pendingResource.hasShieldingChanges && !pendingResource.shieldingApprovedBy);

    return isAuthorized && hasUnapprovedChanges ? pendingResource : undefined;
  }


  async confirmationDialog(val: boolean): Promise<boolean> {
    if (val) {
      const confirmation = this.dialog.open(PendingChangesDialogComponent, {
        height: 'fit-content',
        width: '40%',
        data: {}
      });

      confirmation.afterClosed().subscribe((response: CanDeactivateResponse) => {
        switch (response) {
          case CanDeactivateResponse.Discard:
            return true;
          default:
            return false;
        }
      });
    } else {
      return true;
    }
    return false;
  }


  setUpdaterAndApprovalNames(beamlineShutter: BeamlineShutterRelation) {
    const detailUsers = new DetailsUsers();
    const pendingResources = beamlineShutter?.pendingResources;
    if (pendingResources?.length) {
      //Resource
      const lastApproved = pendingResources
        .filter(p => (p.hasResourceChanges || !p.hasShieldingChanges) && (p.type == PendingResourcesType.Details || p.type == PendingResourcesType.Both) && ((p.status === 2 && !!p.statusBy) || !!p.approvedBy))
        .at(-1);

      detailUsers.approverUserName = this.getUser(lastApproved?.approvedBy ?? lastApproved?.statusBy)?.name;
      detailUsers.approvedOn = lastApproved?.approvedOn ?? lastApproved?.statusOn;
      detailUsers.updateUserName = this.getUser(lastApproved?.createdBy)?.name;
      detailUsers.updatedOn = lastApproved?.createdOn;

      //Shielding
      const lastApprovedS = pendingResources
        .filter(p => p.hasShieldingChanges && (p.type == PendingResourcesType.Details || p.type == PendingResourcesType.Both) && ((p.status === 2 && !!p.statusBy) || !!p.approvedBy))
        .at(-1);

      detailUsers.sapproverUserName = this.getUser(lastApprovedS?.shieldingApprovedBy)?.name;
      detailUsers.sapprovedOn = lastApprovedS?.shieldingApprovedOn;
      detailUsers.supdateUserName = this.getUser(lastApprovedS?.createdBy)?.name;
      detailUsers.supdatedOn = lastApprovedS?.createdOn;
      return detailUsers;
    }
    return {};
  }

}
